import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, ExternalLink, Minus, RadioTower, Waypoints } from 'lucide-react';
import { Avatar, Card, EmptyState, Pill, SegmentedControl, Select, Skeleton } from '../../../components/ui';
import type { IssueView, MovementEntry } from '../../../types/issues';
import {
  CONFIDENCE_LABEL,
  DIRECTION_LABEL,
  DIRECTION_TONE,
  SOURCE_LABEL,
  movementDirection,
  subjectHref,
  subjectKey,
  timeAgo,
} from '../components/format';
import { PositionChange } from '../components/PositionMark';
import { useMovements } from '../useIssues';
import { DirectionFilter, countDirections, filterMovements, groupByDay, subjectsIn } from './timeline';

const DIRECTIONS: { value: DirectionFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'towards', label: 'Towards' },
  { value: 'away', label: 'Away' },
];

const RANGES: { value: string; label: string }[] = [
  { value: '14', label: '14d' },
  { value: '30', label: '30d' },
  { value: '90', label: '90d' },
  { value: 'all', label: 'All time' },
];

const DIRECTION_ICON = {
  towards: ArrowUpRight,
  away: ArrowDownRight,
  first: Minus,
};

/**
 * Who moved, when, from what to what, and what it rested on. Direction is
 * measured against our own position, so "towards us" means the same thing
 * whether we are for the thing or against it.
 */
export const MovementTimeline: React.FC<{ issue: IssueView }> = ({ issue }) => {
  const [range, setRange] = useState('30');
  const [direction, setDirection] = useState<DirectionFilter>('all');
  const [subject, setSubject] = useState<string>('all');
  const { movements, loading, error, unavailable } = useMovements(issue.id, range === 'all' ? null : Number(range));

  const subjects = useMemo(() => subjectsIn(movements), [movements]);
  const filtered = useMemo(() => filterMovements(movements, direction, subject), [movements, direction, subject]);
  const days = useMemo(() => groupByDay(filtered), [filtered]);
  const counts = useMemo(() => countDirections(movements), [movements]);

  if (unavailable) {
    return (
      <Card>
        <EmptyState icon={Waypoints} title="Issues are not available on this server yet" />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl label="Direction" items={DIRECTIONS} value={direction} onChange={setDirection} />
        <SegmentedControl label="Range" items={RANGES} value={range} onChange={setRange} />
        <Select
          aria-label="Entity"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          className="w-full sm:w-52"
        >
          <option value="all">Everyone</option>
          {subjects.map((ref) => (
            <option key={subjectKey(ref)} value={subjectKey(ref)}>
              {ref.label}
            </option>
          ))}
        </Select>
        {movements.length > 0 && (
          <p className="text-[12px] text-neutral-400">
            {movements.length} move{movements.length === 1 ? '' : 's'} · {counts.towards} towards us · {counts.away} away
          </p>
        )}
      </div>

      {error && (
        <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          {error}
        </p>
      )}

      {loading && !movements.length && (
        <Card padded className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </Card>
      )}

      {!loading && !filtered.length && (
        <Card>
          <EmptyState
            icon={Waypoints}
            title={movements.length ? 'Nothing matches these filters' : 'Nobody has moved in this window'}
          />
        </Card>
      )}

      {days.map((day) => (
        <section key={day.key}>
          <h3 className="mb-2 text-[12px] uppercase tracking-[0.06em] text-neutral-400">{day.label}</h3>
          <Card divided>
            {day.entries.map((entry) => (
              <MovementRow key={entry.stance.id} entry={entry} />
            ))}
          </Card>
        </section>
      ))}
    </div>
  );
};

const MovementRow: React.FC<{ entry: MovementEntry }> = ({ entry }) => {
  const { stance } = entry;
  const direction = movementDirection(entry);
  const Icon = DIRECTION_ICON[direction];

  return (
    <div className="flex gap-3 px-4 py-3">
      <Avatar name={stance.subject.label} kind={stance.subject.type} size="sm" className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <Link
            to={subjectHref(stance.subject)}
            className="truncate text-[13px] text-ink transition-colors hover:text-primary-700"
          >
            {stance.subject.label}
          </Link>
          <PositionChange from={entry.from} to={entry.to} />
          <Pill tone={DIRECTION_TONE[direction]}>
            <Icon className="h-3 w-3" strokeWidth={2} />
            {DIRECTION_LABEL[direction]}
          </Pill>
        </div>

        {stance.rationale && <p className="mt-1.5 text-[12.5px] leading-snug text-neutral-600">{stance.rationale}</p>}

        <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-neutral-400">
          <span title={stance.asOf ? new Date(stance.asOf).toLocaleString() : undefined}>{timeAgo(stance.asOf)}</span>
          <span aria-hidden="true">·</span>
          <span>{SOURCE_LABEL[stance.source]}</span>
          <span aria-hidden="true">·</span>
          <span>{CONFIDENCE_LABEL[stance.confidence]} confidence</span>
          {stance.recordedBy && (
            <>
              <span aria-hidden="true">·</span>
              <span>{stance.recordedBy.name}</span>
            </>
          )}
          {stance.evidenceUrl && (
            <a
              href={stance.evidenceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
            >
              <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
              Evidence
            </a>
          )}
          {!stance.evidenceUrl && stance.signalId && (
            <span className="inline-flex items-center gap-1 text-neutral-500" title="Came from a monitoring signal">
              <RadioTower className="h-3 w-3" strokeWidth={1.75} />
              From a monitoring signal
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

export default MovementTimeline;
